import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Button } from "wiz-front-shared/components/button/Button";
import { TermCheckBox } from "wiz-front-shared/components/termCheckBox/TermCheckBox";
import { userStore } from "store/userStore";
import {
    withdrawDataAtom,
    pushWithdrawStepAtom,
    replaceWithdrawStepAtom,
    TermsConsentItem,
} from "store/withdrawStore";
import { getStoredServiceId } from "common/app";
import './Withdraw.css';

interface TermsItem {
    termsId: number;
    serviceId: string;
    locale: string;
    termsCode: string;
    termsTitle: string;
    termsVersion: string;
    isRequired: "Y" | "N";
}

const TermItem = ({
    term,
    checked,
    onCheck,
    onOpenDetail,
    termsDetail,
    isDetailLoading,
}: {
    term: TermsItem;
    checked: boolean;
    onCheck: (termsId: number, checked: boolean) => void;
    onOpenDetail: (termsId: number) => void;
    termsDetail: { termsTitle?: string; termsContent?: string } | null;
    isDetailLoading: boolean;
}) => {
    const [isChecked, setIsChecked] = useState(checked);

    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);

    return (
        <TermCheckBox
            title={term.termsTitle}
            id={`withdraw_term_${term.termsId}`}
            checked={isChecked}
            setChecked={setIsChecked}
            isRequired={term.isRequired === "Y"}
            onChange={(e) => onCheck(term.termsId, e.target.checked)}
            onOpenDetail={() => onOpenDetail(term.termsId)}
            onAgree={() => onCheck(term.termsId, true)}
            termsTitle={termsDetail?.termsTitle}
            termsContent={termsDetail?.termsContent}
            isDetailLoading={isDetailLoading}
        />
    );
};

export const WithdrawStep1 = () => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useAtom(withdrawDataAtom);
    const pushStep = useSetAtom(pushWithdrawStepAtom);
    const replaceStep = useSetAtom(replaceWithdrawStepAtom);

    const termsState = useAtomValue(userStore.withdrawTerms.stateAtom);
    const termsDetailState = useAtomValue(userStore.termsItem.stateAtom);

    // 약관 체크 상태 — 진입 시 withdrawData.termsConsents 로부터 복원 (Step2 → device back 시 체크 유지)
    const [checkedTerms, setCheckedTerms] = useState<Record<number, boolean>>(() => {
        const initial: Record<number, boolean> = {};
        data.termsConsents.forEach(tc => {
            initial[tc.termsId] = true;
        });
        return initial;
    });
    const [currentDetailTermsId, setCurrentDetailTermsId] = useState<number | null>(null);
    const skippedRef = useRef(false);

    // 약관 조회 — 이미 ready 면 skip (재진입 시 체크박스 깜빡임 방지). locale 변경 시는 재조회.
    useEffect(() => {
        const serviceId = getStoredServiceId();
        if (!serviceId) return;
        if (termsState.status === "ready" && termsState.data) return;
        userStore.onGetWithdrawTerms(serviceId, i18n.language || "ko");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const termsList: TermsItem[] = useMemo(() => {
        return termsState.data?.data || [];
    }, [termsState.data]);

    const requiredTerms = useMemo(
        () => termsList.filter(t => t.isRequired === "Y"),
        [termsList]
    );

    const allRequiredChecked = useMemo(() => {
        if (requiredTerms.length === 0) return termsList.length > 0; // 모두 선택 약관일 땐 그냥 통과
        return requiredTerms.every(t => checkedTerms[t.termsId]);
    }, [requiredTerms, termsList, checkedTerms]);

    // 약관 0개일 때 자동 스킵 (Step1 → Step2 replace, 뒤로가기로 다시 돌아오지 않게)
    useEffect(() => {
        if (!termsState.isReady || skippedRef.current) return;
        if (termsList.length === 0) {
            skippedRef.current = true;
            replaceStep(2);
        }
    }, [termsState.isReady, termsList, replaceStep]);

    const handleTermCheck = useCallback((termsId: number, checked: boolean) => {
        setCheckedTerms(prev => ({ ...prev, [termsId]: checked }));
    }, []);

    const handleOpenDetail = useCallback((termsId: number) => {
        setCurrentDetailTermsId(termsId);
        userStore.onGetTermsDetailConsent(String(termsId));
    }, []);

    const getTermsDetail = useCallback((termsId: number) => {
        if (currentDetailTermsId === termsId && termsDetailState.isReady) {
            return termsDetailState.data?.data || termsDetailState.data;
        }
        return null;
    }, [currentDetailTermsId, termsDetailState]);

    const handleNext = () => {
        const consents: TermsConsentItem[] = termsList
            .filter(term => checkedTerms[term.termsId])
            .map(term => ({ termsId: term.termsId, termsVersion: term.termsVersion }));

        setData(prev => ({ ...prev, termsConsents: consents }));
        pushStep(2);
    };

    return (
        <div className="withdraw-step">
            <span className="title">{t("withdraw.step1.title")}</span>
            <span className="info">{t("withdraw.step1.info")}</span>

            {termsState.isPending && (
                <div className="withdraw-terms-loading">{t("common.loading")}</div>
            )}

            <div style={{ display: "flex", flexDirection: "column", marginTop: 24 }}>
                {termsList.map(term => (
                    <TermItem
                        key={term.termsId}
                        term={term}
                        checked={checkedTerms[term.termsId] || false}
                        onCheck={handleTermCheck}
                        onOpenDetail={handleOpenDetail}
                        termsDetail={getTermsDetail(term.termsId)}
                        isDetailLoading={currentDetailTermsId === term.termsId && termsDetailState.isPending}
                    />
                ))}
            </div>

            <div className="withdraw-bottom-btn">
                <Button
                    text={t("withdraw.btnNext")}
                    onPress={handleNext}
                    disabled={!allRequiredChecked}
                />
            </div>
        </div>
    );
};
